// @ts-nocheck
import React, { cloneElement, Fragment, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import {
    Button,
    ClickAwayListener,
    Fade,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';

const actionsStyles = (theme) => ({
    icons: {
        marginRight: 5,
        fontSize: 16,
    },
    cardActions: {
        width: '100%',
        marginLeft: -20,
        marginTop: 5,
        marginBottom: 5,
        justifyContent: 'flex-start',
        zIndex: 1,
    },
    menuListRoot: {
        padding: 0,
    },
    menuListItemRoot: {
        marginRight: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
    },
    menuRoot: {
        padding: '0 16px 0 12px',
    },
    menuIcon: {
        fontSize: 20,
    },
    menuTextRoot: {
        padding: 0,
    },
    menuText: {
        fontSize: '0.9375rem',
        fontWeight: 500,
    },
    rootCheckbox: {
        padding: 0,
        marginRight: theme.spacing.unit,
    },
    multiSelectLabel: {
        color: theme.palette.primary.main,
        fontSize: 13,
        fontWeight: 500,
    },
    multiSelectLabelCheckbox: {
        padding: 6,
        color: theme.palette.primary.main,
    },
    formControlLabelRoot: {
        marginLeft: -5,
        marginRight: 5,
    },
    dialogTitle: {
        padding: '17px 20px 0 20px',
        paddingBottom: 15,
    },
    dialogTitleText: {
        fontWeight: 'bold',
        fontSize: 17,
    },
    container: {
        display: 'grid',
        gridTemplateColumns: '30px auto',
        alignItems: 'center',
        columnGap: '7px',
    },
    dialogActions: {
        marginRight: 20,
        marginBottom: 20,
    },
    button: {
        paddingLeft: 30,
        paddingRight: 30,
        marginLeft: 10,
    },
    circularProgress: {
        marginTop: '20px',
        marginBottom: '20px',
        overflowY: 'hidden',
    },
    dialogContent: {
        paddingBottom: 0,
    },
    dialogText: {
        color: 'black',
        fontSize: 13,
    },
    buttonMenu: {
        paddingLeft: 10,
        fontSize: 13,
    },
});

const useStyles = makeStyles(actionsStyles as any);

const hasRoles = (roles = [], permissions = [], allOfRolesInPermissions = true) => {
    if (allOfRolesInPermissions)
        return roles.every(
            (role) => -1 !== permissions.findIndex((prole) => prole.toLowerCase() === role.toLowerCase()),
        );
    return roles.reduce((found, role) => {
        return found ? found : permissions.includes(role);
    }, false);
};

const filterMenuByPermissions = (menuList, permissions) =>
    menuList.filter((item) => {
        if (item.hasOwnProperty('renderComponent')) return true;

        return hasRoles(item.roles, permissions, true);
    });

// TypeScript interfaces for props and action items
interface MenuActionItem {
    icon?: React.ReactElement;
    text?: string;
    onClick?: () => void;
    disabled?: boolean | ((roles?: string[], permissions?: string[]) => boolean);
    roles?: string[];
    renderComponent?: React.ReactElement;
    dataTestSC?: string;
}

interface MenuButtonActionsProps {
    disabled?: boolean;
    buttonLabel?: string;
    overwriteButtonLabel?: boolean;
    menuActions?: MenuActionItem[];
    buttonProps?: any;
    permissions?: string[];
    dataTestSCMenuButton?: string;
    customButton?: React.ReactElement;
}

const MenuButtonActions = ({
    disabled = false,
    buttonLabel,
    overwriteButtonLabel,
    menuActions = [],
    buttonProps,
    permissions,
    dataTestSCMenuButton,
    customButton,
}: MenuButtonActionsProps) => {
    const classes = useStyles();
    const translate = useTranslate();
    const filteredMenuActions = filterMenuByPermissions(menuActions, permissions);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuState = Boolean(anchorEl);

    const handleToggle = (event) => {
        setAnchorEl(menuState ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Fragment>
            {customButton ? (
                cloneElement(customButton, {
                    onClick: handleToggle,
                    disabled: disabled || filteredMenuActions.length === 0,
                })
            ) : (
                <Button
                    color={'primary'}
                    disabled={disabled || filteredMenuActions.length === 0}
                    onClick={handleToggle}
                    className={classes.buttonMenu}
                    {...(buttonProps ? buttonProps : {})}
                    data-test-sc={dataTestSCMenuButton ? dataTestSCMenuButton : 'menuButtonActionsButton'}
                >
                    <MoreVertIcon className={classes.icons} data-test-sc='menuButtonActionsIcon' />
                    {overwriteButtonLabel ? buttonLabel : translate('app.main_buttons.actions')}
                </Button>
            )}
            <Popper
                open={menuState}
                anchorEl={anchorEl}
                transition
                placement={'bottom-start'}
                style={{ zIndex: 9999 }}
                data-test-sc='menuButtonActionsPopper'
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    className={classes.menuListRoot}
                                    data-test-sc='menuButtonActionsMenuList'
                                    data-testid='menuButtonActionsMenuList'
                                >
                                    {filteredMenuActions.map((item, index) =>
                                        !item.hasOwnProperty('renderComponent') ? (
                                            <MenuItem
                                                key={index}
                                                onClick={() => {
                                                    item.onClick();
                                                    handleClose();
                                                }}
                                                className={classes.menuRoot}
                                                disabled={
                                                    typeof item.disabled === 'function'
                                                        ? item.disabled(item.roles, permissions)
                                                        : item.disabled
                                                }
                                                data-test-sc={item.dataTestSC ? item.dataTestSC : 'menuItem'}
                                            >
                                                <ListItemIcon className={classes.menuListItemRoot}>
                                                    {item.icon && (
                                                        item.icon.type === 'img' ? (
                                                            // Para iconos de imagen (SVG, PNG, etc.)
                                                            <div style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                width: 24,
                                                                height: 24
                                                            }}>
                                                                {item.icon}
                                                            </div>
                                                        ) : (
                                                            // Para iconos de Material-UI
                                                            cloneElement(item.icon, {
                                                                ...item.icon.props,
                                                                color: 'primary',
                                                                className: classes.menuIcon,
                                                            })
                                                        )
                                                    )}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={translate(item.text)}
                                                    className={classes.menuTextRoot}
                                                    primaryTypographyProps={{
                                                        className: classes.menuText,
                                                            color: 'textPrimary',
                                                    }}
                                                    data-test-sc={
                                                        item.dataTestSC ? `${item.dataTestSC}Text` : 'menuItemText'
                                                    }
                                                />
                                            </MenuItem>
                                        ) : (
                                            item.renderComponent
                                        ),
                                    )}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Fragment>
    );
};

export default MenuButtonActions;
